import { format } from 'date-fns';
import {
  BillStatus,
  IdBill,
  IdLeaseAgreement,
  IDTransactionLog,
  IProfile,
  IServiceFeeCalculation,
  ServiceFeeRule,
  TransactionStatus,
  TrxLogType,
  Vendors,
} from 'realhaus-sdk';
import { IsSystemWallet } from '../global/util';
import { formatAddress, streetAddress } from './address';
import { billTypeLabel, paymentMethodLabel } from './bill';

/**
 * How transactions read to the people in them: one place for the titles, explanations and
 * details shown in the transaction list, its details drawer and the dashboard. Everything is
 * worked out from what the viewer can read (the transaction log, its bill and lease, and the
 * people involved) and reads from the viewer's side.
 */

/** A transaction log with what it refers to, as far as it could be loaded. */
export type Activity = {
  trx: IDTransactionLog;
  bill?: IdBill;
  lease?: IdLeaseAgreement;
  counterparty?: IProfile; // who paid the viewer, or whom the viewer paid
};

export enum TransactionKind {
  SERVICE_FEE = 'SERVICE_FEE',
  BILL_PAYMENT_RECEIVED = 'BILL_PAYMENT_RECEIVED',
  BILL_PAYMENT_SENT = 'BILL_PAYMENT_SENT',
  CREDIT_REPORT_SEATS = 'CREDIT_REPORT_SEATS',
  PAYOUT = 'PAYOUT',
  REFUND = 'REFUND',
  TRANSFER_RECEIVED = 'TRANSFER_RECEIVED',
  TRANSFER_SENT = 'TRANSFER_SENT',
  OTHER = 'OTHER',
}

export type Direction = 'in' | 'out';
export type StatusTone = 'success' | 'warning' | 'error';

/** Text colours for each status tone, dark enough to read on white. */
export const STATUS_TONE_COLORS: Record<StatusTone, string> = {
  success: '#1B7F3A',
  warning: '#B54708',
  error: '#B42318',
};

export interface TransactionDescription {
  kind: TransactionKind;
  title: string;
  subtitle?: string;
  direction: Direction;
  amount: number; // what moved for the viewer, never negative
  status: { label: string; tone: StatusTone };
  paidOutsideRealhaus: boolean;
}

/** Transactions related to the one being shown, loaded for its details. */
export interface RelatedTransactions {
  payment?: Activity; // the payment a service fee was withheld from, or a refund refunds
  serviceFee?: IDTransactionLog; // the service fee charged on a rent payment received
  payout?: IDTransactionLog; // the payout a service fee for rent paid outside Realhaus came out of
  fees?: Activity[]; // the service fees deducted from a payout
  billParty?: IProfile; // the other person on the bill: the tenant, for a service fee
}

export type DetailLink =
  | { kind: 'lease'; leaseId: string; isOwner: boolean }
  | { kind: 'transaction'; activity: Activity };

export interface TransactionDetailRow {
  label: string;
  value: string;
  link?: DetailLink;
}

const PCOMMERCE = 'PCOMMERCE';

/** How a payout reaches the user, by the vendor that paid it out. */
const PAYOUT_METHODS: Record<string, { title: string; how: string; row: [string, string] }> = {
  [Vendors.STRIPE]: {
    title: 'Payout to your bank account',
    how: 'to your bank account',
    row: ['Paid out to', 'Your bank account'],
  },
  [PCOMMERCE]: {
    title: 'Payout by Interac e-Transfer',
    how: 'by Interac e-Transfer',
    row: ['Paid out by', 'Interac e-Transfer'],
  },
};

const cad = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });

export const formatCurrency = (amount: number) =>
  cad.format(Number.isFinite(Number(amount)) ? Number(amount) : 0);

export const formatSignedAmount = (amount: number, direction: Direction) =>
  `${direction === 'in' ? '+' : '−'}${formatCurrency(Math.abs(amount))}`;

const formatPercent = (fraction: number) => `${Number((fraction * 100).toFixed(2))}%`;

const formatDate = (timestamp: number) => format(timestamp, 'PP');

const personName = (profile?: IProfile) => {
  const name = [profile?.firstname, profile?.lastname].filter(Boolean).join(' ').trim();
  return name || undefined;
};

const joinParts = (...parts: (string | false | undefined)[]) =>
  parts.filter((p): p is string => !!p).join(' · ') || undefined;

export const transactionStatus = (
  status: TransactionStatus,
): { label: string; tone: StatusTone } => {
  switch (status) {
    case TransactionStatus.SUCCESS:
      return { label: 'Completed', tone: 'success' };
    case TransactionStatus.FAILED:
      return { label: 'Failed', tone: 'error' };
    default:
      return { label: 'In progress', tone: 'warning' };
  }
};

export const getTransactionKind = (trx: IDTransactionLog, viewerId: string): TransactionKind => {
  const isSender = trx.senderId === viewerId;
  const isReceiver = trx.receiverId === viewerId;

  switch (trx.type) {
    case TrxLogType.WITHDRAW:
      return TransactionKind.PAYOUT;
    case TrxLogType.REFUND:
      return TransactionKind.REFUND;
    case TrxLogType.PAYMENT:
      if (trx.meta?.creditReportSeatId) return TransactionKind.CREDIT_REPORT_SEATS;
      if (isReceiver) return TransactionKind.BILL_PAYMENT_RECEIVED;
      if (isSender) return TransactionKind.BILL_PAYMENT_SENT;
      return TransactionKind.OTHER;
    case TrxLogType.TRANSFER:
      // The only transfer a user makes to Realhaus is the landlord's service fee on rent.
      if (isSender && IsSystemWallet(trx.receiverId)) return TransactionKind.SERVICE_FEE;
      if (isReceiver) return TransactionKind.TRANSFER_RECEIVED;
      if (isSender) return TransactionKind.TRANSFER_SENT;
      return TransactionKind.OTHER;
    default:
      return TransactionKind.OTHER;
  }
};

/**
 * A service fee for rent paid outside Realhaus: it has a bill but no payment. This is the rule the
 * payout job uses to net it; meta.offPlatform is only set on fees charged since October 2026.
 */
export const isOffPlatformServiceFee = (trx: IDTransactionLog) =>
  trx.meta?.offPlatform === true || (!trx.meta?.trxLogRef && !!trx.meta?.billId);

/** Service fees charged before January 2025 were not linked to their rent bill. */
const isUnlinkedServiceFee = (trx: IDTransactionLog) => !trx.meta?.billId;

/** What a received payment adds to the viewer's balance: the bill, without the payer's fee. */
const receivedAmount = (trx: IDTransactionLog) => trx.amount - (trx.meta?.trxfees ?? 0);

export const serviceFeeCalculationText = (calculation?: IServiceFeeCalculation) => {
  if (!calculation) return undefined;
  if (calculation.rule === ServiceFeeRule.PERCENT && calculation.percent !== undefined) {
    return `${formatPercent(calculation.percent)} of ${formatCurrency(
      calculation.basis,
    )} rent, as rent is over ${formatCurrency(calculation.flatFeeLimit)}`;
  }
  return `${formatCurrency(
    calculation.flatFee ?? calculation.amount,
  )} flat fee for rent up to ${formatCurrency(calculation.flatFeeLimit)}`;
};

export const describeTransaction = (
  activity: Activity,
  viewerId: string,
): TransactionDescription => {
  const { trx, bill, lease, counterparty } = activity;
  const kind = getTransactionKind(trx, viewerId);
  const status = transactionStatus(trx.status);
  const name = personName(counterparty);
  const billLabel = bill ? billTypeLabel(bill.type, bill.note) : 'Bill';
  const street = lease?.listingInfo?.address
    ? streetAddress(lease.listingInfo.address) ?? undefined
    : undefined;
  const due = bill ? `Due ${formatDate(bill.dueDate)}` : undefined;
  const base = { kind, status, paidOutsideRealhaus: false };

  switch (kind) {
    case TransactionKind.SERVICE_FEE: {
      const paidOutsideRealhaus = isOffPlatformServiceFee(trx);
      return {
        ...base,
        title: 'Service fee for rent',
        subtitle: joinParts(
          street,
          bill && `Rent due ${formatDate(bill.dueDate)}`,
          paidOutsideRealhaus && 'Paid outside Realhaus',
        ),
        direction: 'out',
        amount: trx.amount,
        paidOutsideRealhaus,
      };
    }
    case TransactionKind.BILL_PAYMENT_RECEIVED:
      return {
        ...base,
        title: name ? `${billLabel} from ${name}` : `${billLabel} received`,
        subtitle: joinParts(street, due),
        direction: 'in',
        amount: receivedAmount(trx),
      };
    case TransactionKind.BILL_PAYMENT_SENT:
      return {
        ...base,
        title: name ? `${billLabel} payment to ${name}` : `${billLabel} payment`,
        subtitle: joinParts(street, due),
        direction: 'out',
        amount: trx.amount,
      };
    case TransactionKind.CREDIT_REPORT_SEATS: {
      const seats = trx.meta?.numberOfSeats;
      return {
        ...base,
        title: seats ? `Credit report seats (${seats})` : 'Credit report seats',
        direction: 'out',
        amount: trx.amount,
      };
    }
    case TransactionKind.PAYOUT: {
      const fees = Number(trx.meta?.offPlatformFeeTotal ?? 0);
      return {
        ...base,
        title: PAYOUT_METHODS[trx.source]?.title ?? 'Payout',
        subtitle: fees > 0 ? `${formatCurrency(fees)} in service fees deducted` : undefined,
        direction: 'out',
        amount: trx.amount,
      };
    }
    case TransactionKind.REFUND:
      return {
        ...base,
        title: 'Refund',
        direction: trx.receiverId === viewerId ? 'in' : 'out',
        amount: trx.amount,
      };
    case TransactionKind.TRANSFER_RECEIVED:
      return {
        ...base,
        title: name ? `Money from ${name}` : 'Money received',
        direction: 'in',
        amount: trx.amount,
      };
    case TransactionKind.TRANSFER_SENT:
      return {
        ...base,
        title: name ? `Money sent to ${name}` : 'Money sent',
        direction: 'out',
        amount: trx.amount,
      };
    default:
      return {
        ...base,
        title: trx.narration || 'Transaction',
        direction: trx.receiverId === viewerId ? 'in' : 'out',
        amount: trx.amount,
      };
  }
};

/** A short explanation of the transaction, in plain words, for its details. */
export const explainTransaction = (
  activity: Activity,
  related: RelatedTransactions,
  viewerId: string,
): string | undefined => {
  const { trx } = activity;
  const failed = trx.status === TransactionStatus.FAILED;
  const inProgress = !failed && trx.status !== TransactionStatus.SUCCESS;

  switch (getTransactionKind(trx, viewerId)) {
    case TransactionKind.SERVICE_FEE:
      if (isUnlinkedServiceFee(trx)) {
        return "Realhaus's service fee on rent. It was charged before Realhaus linked fees to their rent bill, so the bill can't be shown.";
      }
      if (isOffPlatformServiceFee(trx)) {
        return `You marked this rent as paid outside Realhaus. The service fee applies to rent on leases created on Realhaus, however the rent is paid, so Realhaus ${
          related.payout ? 'took it from your payout' : 'deducts it from your next payout'
        }.`;
      }
      return "Realhaus's service fee on this rent payment. It was taken out of the payment before it was paid out to you.";
    case TransactionKind.BILL_PAYMENT_RECEIVED:
      return `Paid to you through Realhaus.${
        related.serviceFee
          ? " Realhaus's service fee is taken out of it before it is paid out to you."
          : ''
      }`;
    case TransactionKind.BILL_PAYMENT_SENT:
      if (failed) return "This payment didn't go through, so the bill is still unpaid.";
      if (inProgress) {
        return 'You paid this bill through Realhaus. The payment provider is still confirming it (bank payments can take a few business days), and the bill shows In Progress until then.';
      }
      return 'You paid this bill through Realhaus.';
    case TransactionKind.CREDIT_REPORT_SEATS:
      return 'You bought credit report seats, used to request applicants\' credit reports.';
    case TransactionKind.PAYOUT: {
      const how = PAYOUT_METHODS[trx.source]?.how ?? 'to you';
      const fees = Number(trx.meta?.offPlatformFeeTotal ?? 0);
      const feeNote =
        fees > 0
          ? ` It was reduced by ${formatCurrency(
              fees,
            )} in service fees for rent marked paid outside Realhaus.`
          : '';
      if (failed) return 'This payout failed, so the amount went back to your balance.';
      if (inProgress) return `Realhaus is paying out your balance ${how}.${feeNote}`;
      return `Realhaus paid out your balance ${how}.${feeNote}`;
    }
    case TransactionKind.REFUND:
      return 'A refund of a payment made through Realhaus.';
    case TransactionKind.TRANSFER_RECEIVED:
    case TransactionKind.TRANSFER_SENT:
      return 'Money moved between Realhaus balances.';
    default:
      return undefined;
  }
};

/** The bill's status as the Bills tab names it. */
const billStatusLabel = (bill: IdBill) => {
  switch (bill.status) {
    case BillStatus.PAID:
      return 'Paid';
    case BillStatus.CANCELED:
      return 'Canceled';
    default:
      return bill.dueDate < Date.now() ? 'Overdue' : 'Pending';
  }
};

const billValue = (bill: IdBill) =>
  joinParts(
    billTypeLabel(bill.type, bill.note),
    formatCurrency(bill.amount),
    `due ${formatDate(bill.dueDate)}`,
    billStatusLabel(bill),
  ) ?? '';

/** The details shown for a transaction, row by row; rows that don't apply are left out. */
export const transactionDetailRows = (
  activity: Activity,
  related: RelatedTransactions,
  viewerId: string,
): TransactionDetailRow[] => {
  const { trx, bill, lease, counterparty } = activity;
  const kind = getTransactionKind(trx, viewerId);
  const rows: TransactionDetailRow[] = [];
  const add = (label: string, value: string | undefined, link?: DetailLink) => {
    if (value) rows.push({ label, value, link });
  };

  const leaseLink: DetailLink | undefined = lease
    ? { kind: 'lease', leaseId: lease.id, isOwner: lease.ownerId === viewerId }
    : undefined;
  if (lease?.listingInfo?.address) add('Property', formatAddress(lease.listingInfo.address));
  if (bill) add(kind === TransactionKind.SERVICE_FEE ? 'Rent bill' : 'Bill', billValue(bill), leaseLink);

  switch (kind) {
    case TransactionKind.SERVICE_FEE: {
      add('Tenant', personName(related.billParty));
      if (isOffPlatformServiceFee(trx) && !isUnlinkedServiceFee(trx)) {
        add('How the rent was paid', 'Outside Realhaus (marked paid by you)');
      } else if (related.payment) {
        const payment = related.payment.trx;
        add(
          'How the rent was paid',
          paymentMethodLabel(payment.meta?.paymentMethod) ?? 'Through Realhaus',
        );
        add(
          'Taken from',
          `${personName(related.payment.counterparty) ?? 'The'}${
            related.payment.counterparty ? "'s" : ''
          } payment of ${formatCurrency(receivedAmount(payment))} on ${formatDate(
            payment.timestamp,
          )}`,
          { kind: 'transaction', activity: related.payment },
        );
      }
      const calculation = serviceFeeCalculationText(trx.meta?.serviceFee);
      add(
        'Fee',
        calculation ?? (bill ? `${formatCurrency(trx.amount)} on rent of ${formatCurrency(bill.amount)}` : undefined),
      );
      if (isOffPlatformServiceFee(trx) && !isUnlinkedServiceFee(trx)) {
        const payout = related.payout;
        if (payout) {
          const payoutStatus =
            payout.status === TransactionStatus.SUCCESS
              ? ''
              : ` (${transactionStatus(payout.status).label.toLowerCase()})`;
          add(
            'Collected',
            `From your payout of ${formatCurrency(payout.amount)} on ${formatDate(
              payout.timestamp,
            )}${payoutStatus}`,
            { kind: 'transaction', activity: { trx: payout } },
          );
        } else {
          add('Collected', 'Not yet. Realhaus deducts it from your next payout');
        }
      }
      break;
    }
    case TransactionKind.BILL_PAYMENT_RECEIVED: {
      add('Paid by', personName(counterparty));
      add('Payment method', paymentMethodLabel(trx.meta?.paymentMethod));
      add('Amount', formatCurrency(receivedAmount(trx)));
      if (related.serviceFee) {
        add('Service fee', `−${formatCurrency(related.serviceFee.amount)}`, {
          kind: 'transaction',
          activity: { trx: related.serviceFee, bill, lease },
        });
        add('You receive', formatCurrency(receivedAmount(trx) - related.serviceFee.amount));
      }
      break;
    }
    case TransactionKind.BILL_PAYMENT_SENT: {
      const fee = Number(trx.meta?.trxfees ?? 0);
      add('Paid to', personName(counterparty));
      add('Payment method', paymentMethodLabel(trx.meta?.paymentMethod));
      add('Bill amount', formatCurrency(trx.amount - fee));
      if (fee > 0) add('Transaction fee', formatCurrency(fee));
      add('Total charged', formatCurrency(trx.amount));
      break;
    }
    case TransactionKind.CREDIT_REPORT_SEATS: {
      const fee = Number(trx.meta?.trxfees ?? 0);
      add('Seats', trx.meta?.numberOfSeats ? `${trx.meta.numberOfSeats}` : undefined);
      add('Payment method', paymentMethodLabel(trx.meta?.paymentMethod));
      if (fee > 0) add('Transaction fee', formatCurrency(fee));
      add('Total charged', formatCurrency(trx.amount));
      break;
    }
    case TransactionKind.PAYOUT: {
      const fees = Number(trx.meta?.offPlatformFeeTotal ?? 0);
      const method = PAYOUT_METHODS[trx.source];
      if (method) add(...method.row);
      if (fees > 0) add('Service fees deducted', formatCurrency(fees));
      (related.fees ?? []).forEach((fee) =>
        add(
          'Service fee',
          joinParts(formatCurrency(fee.trx.amount), describeTransaction(fee, viewerId).subtitle) ??
            formatCurrency(fee.trx.amount),
          { kind: 'transaction', activity: fee },
        ),
      );
      break;
    }
    case TransactionKind.REFUND:
      if (related.payment) {
        add(
          'Refund of',
          `Payment of ${formatCurrency(related.payment.trx.amount)} on ${formatDate(
            related.payment.trx.timestamp,
          )}`,
          { kind: 'transaction', activity: related.payment },
        );
      }
      break;
    case TransactionKind.TRANSFER_RECEIVED:
      add('From', personName(counterparty));
      break;
    case TransactionKind.TRANSFER_SENT:
      add('To', personName(counterparty));
      break;
  }

  add('Reference', trx.id);
  return rows;
};
